import React, { SyntheticEvent } from 'react';
import { InfiniteData } from 'react-query';

import camelize from 'camelize';
import { deleteCookie } from 'cookies-next';
import { isFuture, parseISO } from 'date-fns';
import { camelCase } from 'lodash';
import flatten from 'lodash/flatten';
import semver from 'semver';
import snakeize from 'snakeize';

import { FORBIDDEN_WORD } from '@base/constants/text';

import {
  CHATIE_SERVICE_STATE,
  CHEMING_SERVICE_STATE,
  EpisodePurchaseType,
  Platform,
  StorySaleType,
} from '../constants';
import {
  IAnyPagination,
  ICategory,
  IEFinderObject,
  IEpisodeDraft,
  IStoryDetail,
  IWaitfreeStoryDetail,
} from '../model';
import { ParsedStorage } from './localStorage';

export interface CSSProps {
  className?: string;
}

export type OnClickProps<T extends string = 'onClick', E = Element> = Partial<
  Record<T, (e: React.SyntheticEvent<E>) => void>
>;

export type PartialProperty<T, K extends keyof T> = Pick<Partial<T>, K> &
  Omit<T, K>;

export type RequiredProperty<T, K extends keyof T> = T & {
  [Property in K]-?: T[Property];
};

export type GestureHandler = (e: SyntheticEvent<Element, Event>) => void;

export const getAppInfo = (userAgent?: string): [Platform, string] => {
  if (userAgent) {
    const u = userAgent.toLowerCase();
    const iosMatch = u.match(/chatie-ios\/(\d+\.\d+\.\d+)/);
    if (iosMatch) {
      return [Platform.ios, iosMatch[1]];
    }

    const androidMatch = u.match(/chatie-android\/(\d+\.\d+\.\d+)/);
    if (androidMatch) {
      return [Platform.android, androidMatch[1]];
    }
  }

  return [Platform.web, '2.0.0'];
};

export const checkRequiredAppVersion = (
  platform: Platform,
  version: string,
  { iOS: iOSVersion, android: androidVersion }: { iOS: string; android: string }
) => {
  if (platform === Platform.ios) {
    return semver.gte(version, iOSVersion);
  }

  if (platform === Platform.android) {
    return semver.gte(version, androidVersion);
  }

  return true;
};

/** Cookies */
export function getTokenFromCookies({
  token,
  firebaseIdToken,
  accessToken,
}: {
  token?: string;
  firebaseIdToken?: string;
  accessToken?: string;
}) {
  if (firebaseIdToken) {
    return `FIT ${firebaseIdToken}`;
  }

  if (accessToken) {
    return `JWT ${accessToken}`;
  }

  if (token) {
    return token;
  }

  return undefined;
}

export function removeTokenCookies() {
  deleteCookie('firebaseIdToken', { path: '/' });
  deleteCookie('firebaseIdToken', { domain: 'chatie.me', path: '/' });
  deleteCookie('firebaseIdToken', { domain: 'cheming.ai', path: '/' });
  deleteCookie('accessToken', { path: '/' });
  deleteCookie('accessToken', { domain: 'chatie.me', path: '/' });
  deleteCookie('accessToken', { domain: 'cheming.ai', path: '/' });
}

export const toQueryString = (obj: object | null, withQuerySymbol = true) => {
  if (obj === null) {
    return '';
  }

  const snakeObj = snakeize(obj);
  const query = Object.keys(snakeObj)
    .filter(
      (key) => key !== '' && snakeObj[key] !== undefined && snakeObj[key] !== ''
    )
    .map(
      (key) => `${encodeURIComponent(key)}=${encodeURIComponent(snakeObj[key])}`
    )
    .join('&');

  if (!query) {
    return '';
  }

  return withQuerySymbol ? `?${query}` : query;
};

export const stringifyCategory = (category: ICategory) => {
  if (category.parent) {
    return `${category.parent.name} - ${category.name}`;
  }

  return category.name;
};

export function isEnv(env: 'dev' | 'staging' | 'production') {
  return process.env.NEXT_PUBLIC_ENV === env;
}

export function openDeepLink(url: string, router?: { push: (url: string) => void }) {
  if (!url.startsWith('chatie-app://')) {
    return;
  }

  const isNative = !!(
    window.ChatieAndroidInterface ||
    window.webkit?.messageHandlers?.chatie
  );

  if (!isNative && router) {
    // chatie-app://payment?storyId=1 → /payment?storyId=1
    const path = url.replace('chatie-app:/', '');
    router.push(path);
    return;
  }

  document.location.href = url;
}

export function getPaginatedItems<T>(
  pagination?: InfiniteData<IAnyPagination<T>>
): T[] {
  return flatten(pagination?.pages.map((page) => page.items));
}

export function raf(cb: () => void) {
  let tick = false;

  return () => {
    if (tick) {
      return 0;
    }

    tick = true;
    return window.requestAnimationFrame(() => {
      tick = false;
      cb();
    });
  };
}

export function jsonStringify(obj: object, transformSnakecase = true) {
  return JSON.stringify(transformSnakecase ? snakeize(obj) : obj);
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function retry<T>(
  operation: () => Promise<T>,
  count: number,
  delay = 3000,
  error?: Error
): Promise<T> {
  if (count === 0) {
    return Promise.reject(error);
  }

  if (error) {
    await wait(delay);
  }
  return operation().catch((_error) =>
    retry(operation, count - 1, delay, _error)
  );
}

export const isDraftEmpty = (
  draft: IEpisodeDraft | null,
  checkContent = true
) => {
  if (!draft) {
    return true;
  }

  if (Object.keys(draft).length === 0) {
    return true;
  }

  if (checkContent) {
    if (
      !draft.scenes ||
      !draft.scenes[0] ||
      !draft.scenes[0].items ||
      draft.scenes[0].items.length === 0
    ) {
      return true;
    }
  }

  return false;
};

export const toggleArray = <T>(array: Array<T>, item: T) => {
  if (array.findIndex((a) => a === item) > -1) {
    return array.filter((a) => a !== item);
  }
  return array.concat(item);
};

export class HistoryManager {
  private static instance: HistoryManager;

  static getInstance(): HistoryManager {
    if (!this.instance) {
      this.instance = new HistoryManager();
    }
    return this.instance;
  }

  replace = (url: string): Promise<void> => {
    window.history.replaceState({ ...history.state, as: url }, '', url);
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 100);
    });
  };

  replaceShallow = (url: string, _as?: string): Promise<void> => {
    const as = _as || url;

    window.history.replaceState({ ...history.state, url, as }, '', as);
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 100);
    });
  };

  push = (url: string): Promise<void> => {
    window.history.pushState({ ...history.state, as: url }, '', url);
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve();
      }, 100);
    });
  };
}

export const getClientY = <E = MouseEvent | TouchEvent>(
  e: SyntheticEvent<Element, E>
) => {
  if (e.nativeEvent instanceof TouchEvent) {
    return e.nativeEvent.changedTouches[0].clientY;
  }
  return (e.nativeEvent as MouseEvent).clientY;
};

export const ellipsisString = (str: string, maxLength: number) => {
  if (str.length > maxLength) {
    return `${str.substring(0, maxLength)}...`;
  }

  return str;
};

/**
 * 멘션 인코딩(@[type:id:name])을 자르지 않도록 안전한 자름 위치를 찾는다.
 * cutPos가 멘션 인코딩 중간에 걸리면 멘션 시작 전으로 당긴다.
 */
const safeMentionCut = (str: string, cutPos: number): number => {
  // cutPos 근처에 열린 멘션이 있는지 확인: cutPos 이전의 마지막 '@[' 위치
  const searchStart = Math.max(0, cutPos - 50); // 멘션 최대 길이 고려
  const region = str.slice(searchStart, cutPos);
  const lastOpen = region.lastIndexOf('@[');

  if (lastOpen === -1) return cutPos;

  const absOpen = searchStart + lastOpen;
  // '@[' 이후에 닫는 ']'가 cutPos 이전에 있으면 멘션이 이미 닫힌 것
  const closeBracket = str.indexOf(']', absOpen);

  if (closeBracket === -1 || closeBracket >= cutPos) {
    // 멘션이 cutPos에서 잘림 → 멘션 시작 전으로 자름
    return absOpen;
  }

  return cutPos;
};

export const trimString = (str: string, length = 60, newLineCheck = true) => {
  if (newLineCheck) {
    const newlineIndex = str.indexOf('\n');
    if (newlineIndex !== -1) {
      const cut = safeMentionCut(str, newlineIndex);
      return [str.substring(0, cut), str.slice(cut)];
    }
  }

  if (str.length > length) {
    const cut = safeMentionCut(str, length);
    return [str.substring(0, cut), str.slice(cut)];
  }

  return [str, ''];
};

export const trimStringByLine = (str: string, maxLine = 5) => {
  if ((str.match(/\n/g) || []).length < maxLine) {
    return trimString(str, 60, false);
  }

  const lines = str.split('\n');
  return [lines.slice(0, maxLine).join('\n'), lines.slice(maxLine).join('\n')];
};

export const preventDefault = (e: SyntheticEvent) => e.preventDefault();

export const getRandomNumber = (max: number, min = 0) => {
  return min + Math.floor(Math.random() * (max - min));
};

export const lockScroll = () => {
  const scrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollY}px`;
};

export const unlockScroll = () => {
  const scrollY = document.body.style.top;
  document.body.style.position = '';
  document.body.style.top = '';
  window.scrollTo(0, parseInt(scrollY || '0') * -1);
};

export function checkWaitfreeStory(
  story: IStoryDetail
): story is IWaitfreeStoryDetail {
  return story.saleType === StorySaleType.standardWaitfree;
}

type KeyValueEntry = {
  key?: string;
  value?: string;
};

export const convertCamelizedObject = (
  array: KeyValueEntry[]
): Record<string, string> => {
  return array.reduce((obj, item) => {
    if (item.key && item.value) {
      obj[camelCase(item.key)] = item.value;
    }
    return obj;
  }, {} as Record<string, string>);
};

export const convertToObject = <T extends Record<string, string>>(
  array: { key: keyof T; value: T[keyof T] }[] | undefined
): T | Record<string, string> => {
  if (!array?.length) {
    return {};
  }

  const initialObject = {} as T;

  const convertedObject = array.reduce((accumulator, currentItem) => {
    const { key, value } = currentItem;

    return {
      ...accumulator,
      [key]: value,
    };
  }, initialObject);

  return convertedObject;
};

export const getMailDeepLink = ({
  userCode,
  platform,
  os,
  appVersion,
  device,
  isCheming = false,
}: {
  userCode: string;
  platform: Platform;
  os: string;
  appVersion: string;
  device: string;
  isCheming?: boolean;
}) => {
  const to = isCheming ? 'help@eineblu.me' : 'help@eineblu.me';
  const body = `
  
 ⬆️ 문의 사항을 간략하게 제목에 입력해주세요!
  현재 겪고 계신 문제 상황을 자세히 설명해주세요.
  스크린샷을 첨부해주시면 문제 파악에 도움이 됩니다.
  (위의 내용은 지우고 보내주셔도 됩니다.)
  문의 사항:
 
 
  아래 내용을 지우거나 수정하지 말아주세요
  유저코드: ${userCode}
  기기명: ${platform} / ${device}
  OS 버전: ${os}
  앱 버전: ${appVersion}
  `;

  const encodedBody = encodeURIComponent(body);
  const url = `mailto:${to}?body=${encodedBody}`;

  return url;
};
export function getSettingDate() {
  const isMockDate = !isEnv('production');

  if (isMockDate && typeof window !== 'undefined') {
    return ParsedStorage.getItem('mockDate');
  }

  const realDate = new Date();

  return { date: realDate };
}

export const checkEmail = (email: string) => {
  const reg =
    /^[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*@[0-9a-zA-Z]([-_\.]?[0-9a-zA-Z])*\.[a-zA-Z]{2,3}$/i;
  return reg.test(email);
};

export const checkPassword = (password: string) => {
  const reg = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d~`!@#.,:;'"-_{}$%^&*()+|=]{8,}$/;
  return reg.test(password);
};

export const getParams = (query: string | undefined) => {
  if (!query) {
    return {};
  }

  return camelize(
    (/^[?#]/.test(query) ? query.slice(1) : query)
      .split('&')
      .filter((key) => key !== '')
      .reduce((params: { [key: string]: string }, param: string) => {
        const pair = param.split('=');
        const key = pair[0];
        let value = pair[1];
        // value = value.indexOf(',') > -1 ? value.split(',') : value;
        value = value ? decodeURIComponent(value.replace(/\+/g, ' ')) : '';
        params[key] = value;
        return params;
      }, {})
  );
};
export const copyToClipboard = (text: string) => {
  const textarea = document.createElement('textarea');
  document.body.appendChild(textarea);

  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  textarea.value = text;
  try {
    textarea.select();
  } catch (error) {}

  if (navigator.userAgent.match(/ipad|ipod|iphone/i)) {
    textarea.readOnly = true;

    const range = document.createRange();
    range.selectNodeContents(textarea);

    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    textarea.setSelectionRange(0, 99999);
  }

  document.execCommand('copy');
  document.body.removeChild(textarea);
};

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export function isPurchased(
  type?: EpisodePurchaseType,
  expired?: string
): boolean {
  if (!type) {
    return false;
  }

  if (type === EpisodePurchaseType.possession) {
    return true;
  }

  return isFuture(parseISO(expired!));
}

type EFinderModifedImage = {
  url: string;
  resize: (type: 'width' | 'height', size: number) => string;
  og: () => string;
};

export const EFinderImage = (
  imageObj?: IEFinderObject<'image'>
): EFinderModifedImage | undefined => {
  if (!imageObj) {
    return undefined;
  }

  const {
    attachment: { url, contentType, width, height },
  } = imageObj;
  const imageUrl = url.replace(/(\?.*)|(#.*)/g, '');

  return {
    url: imageUrl,
    resize(type, size: number) {
      if (contentType === 'image/gif') {
        return imageUrl;
      }

      if (type === 'height') {
        return `${imageUrl}?t=h&d=${height && size > height ? height : size}`;
      }

      return `${imageUrl}?t=w&d=${width && size > width ? width : size}`;
    },
    og() {
      return `${imageUrl}?t=w&d=1200`;
    },
  };
};

const isEndWithConsonant = (korStr: string) => {
  const finalChrCode = korStr.charCodeAt(korStr.length - 1);
  const finalConsonantCode = (finalChrCode - 44032) % 28;
  return finalConsonantCode !== 0;
};

export type Josa = '은' | '는' | '이' | '가' | '을' | '를' | '와' | '과';

export const getJosa = (korStr: string | undefined, josa: Josa) => {
  if (!korStr) {
    return;
  }

  const isJong = isEndWithConsonant(korStr);

  if (josa == '을' || josa == '를') return korStr + (isJong ? '을' : '를');
  if (josa == '이' || josa == '가') return korStr + (isJong ? '이' : '가');
  if (josa == '은' || josa == '는') return korStr + (isJong ? '은' : '는');
  if (josa == '와' || josa == '과') return korStr + (isJong ? '과' : '와');

  return korStr + josa;
};

export const getJosaOnly = (korStr: string | undefined, josa: Josa) => {
  if (!korStr) {
    return;
  }

  const isJong = isEndWithConsonant(korStr);

  if (josa == '을' || josa == '를') return isJong ? '을' : '를';
  if (josa == '이' || josa == '가') return isJong ? '이' : '가';
  if (josa == '은' || josa == '는') return isJong ? '은' : '는';
  if (josa == '와' || josa == '과') return isJong ? '과' : '와';

  return josa;
};

// 챗봇 플로우 이벤트 트래킹을 위한 function
// 각 bottom tab별로 첫 진입시 isDelete로 초기화 후 플로우 쌓기
// 플로우 마지막 이벤트 발송 후에도 flow 초기화
export const setFlow = ({
  name,
  isDelete,
}: {
  name?: string;
  isDelete?: boolean;
}) => {
  const flow = ParsedStorage.getItem('inflow');
  if (name) {
    if (flow && flow?.length > 0) {
      if (flow[0].includes(name)) {
        return;
      }
      const string = flow[0] + '/' + name;
      ParsedStorage.setItem('inflow', JSON.stringify([string]));
    } else {
      ParsedStorage.setItem('inflow', JSON.stringify([name]));
    }
  }
  if (isDelete) {
    ParsedStorage.removeItem('inflow');
  }
};

export const getFlow = () => {
  const flow = ParsedStorage.getItem('inflow');
  if (flow && flow.length > 0) {
    return flow[0];
  }
  return '';
};

export const formatComma = (number: number) => {
  if (!number) {
    return 0;
  }
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const removeNullFields = (obj: { [x: string]: never }) => {
  Object.keys(obj).forEach((key) => {
    if (obj[key] && typeof obj[key] === 'object') {
      removeNullFields(obj[key]);
    } else if (obj[key] === null) {
      delete obj[key];
    }
  });
};

export const isObjectsEqual = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj1: Record<string, any>,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj2: Record<string, any>
) => {
  // 두 객체의 속성 개수가 다르면 두 객체는 다름
  const obj1Keys = Object.keys(obj1);
  const obj2Keys = Object.keys(obj2);
  if (obj1Keys.length !== obj2Keys.length) {
    return false;
  }

  // 각 속성을 순회하면서 비교
  for (const key of obj1Keys) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    // 값이 객체인 경우, 재귀적으로 비교
    const areObjects = isObject(val1) && isObject(val2);
    if (
      (areObjects && !isObjectsEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false;
    }
  }

  // 모든 속성이 같다면 true 반환
  return true;
};

export const isObject = (object: Record<string, never>) => {
  return object != null && typeof object === 'object';
};
export const checkHasIncludeForBidden = <T>(obj: T) => {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      if (value !== null && typeof value === 'string') {
        for (const keyword of FORBIDDEN_WORD) {
          if (value.includes(keyword)) {
            return true; // 값에서 키워드 발견
          }
        }
      } else if (typeof value === 'object') {
        // 중첩된 객체를 재귀적으로 확인
        if (checkHasIncludeForBidden(value)) {
          return true; // 중첩된 객체에서 키워드 발견
        }
      }
    }
  }
  return false;
};

export const truncateString = (text: string, maxLength: number) => {
  if (text.length <= maxLength) {
    return text;
  }

  const lastSpaceIndex = text.lastIndexOf(' ', maxLength);

  if (lastSpaceIndex !== -1) {
    return text.substring(0, lastSpaceIndex);
  } else {
    return text.substring(0, maxLength);
  }
};

export const compareVersions = (versionA: string, versionB: string) => {
  const partsA = versionA.split('.').map(Number);
  const partsB = versionB.split('.').map(Number);

  for (let i = 0; i < 3; i++) {
    if (partsA[i] > partsB[i]) {
      return true; // versionA가 더 큼
    } else if (partsA[i] < partsB[i]) {
      return false; // versionB가 더 큼
    }
  }

  return true; // 두 버전이 동일함
};

export const getnerateKeyedSeededRandom = (
  min: number,
  max: number,
  seed: string,
  key: number
) => {
  function hashStringWithKey(s: string, key: number): number {
    let hash = key;
    for (let i = 0; i < s.length; i++) {
      const char = s.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash >>>= 0;
    }
    return hash;
  }

  function linearCongruentialGenerator(seed: number): number {
    const a = 1664525;
    const c = 1013904223;
    const m = 2 ** 32;
    const nextSeed = (a * seed + c) % m;
    return nextSeed / m;
  }

  const initialSeed = hashStringWithKey(seed, key);
  const rand = linearCongruentialGenerator(initialSeed);
  const range = max - min + 1;
  let result = Math.floor(rand * range) + min;

  if (result < min) result = min;
  if (result > max) result = max;

  return result;
};

export const juminChecksum = (num: string): boolean => {
  if (num.length !== 13 || !/^\d{13}$/.test(num)) {
    return false; // 유효하지 않은 주민등록번호 형식
  }

  const jumin = num.split('').map(Number);
  const ckarr = [2, 3, 4, 5, 6, 7, 8, 9, 2, 3, 4, 5];
  let sum = 0;

  for (let i = 0; i < ckarr.length; i++) {
    sum += jumin[i] * ckarr[i];
  }

  const checkDigit = (11 - (sum % 11)) % 10;

  return checkDigit === jumin[12];
};

type SimplifyNumberOptions = {
  value: number;
  lng?: string;
};

function getDivisableCount(
  dividend: number,
  divisor: number,
  count = 0
): [number, number, number] {
  const quotient = ~~(dividend / divisor);
  const remainder = dividend % divisor;

  if (quotient > divisor) {
    return getDivisableCount(quotient, divisor, count + 1);
  }

  return [quotient, remainder, count];
}

export const simplifyNumber = ({ value }: SimplifyNumberOptions): string => {
  const lng = 'ko';
  const units: Record<string, string[]> = {
    ko: ['만', '억', '조', '경', '해'],
  };

  const divisors: Record<string, number> = {
    ko: 10000,
  };

  const unit = units[lng] || ['K', 'M', 'B', 'T', 'Q'];
  const divisor = divisors[lng] || 1000;

  const [big, small, index] = getDivisableCount(value, divisor);

  if (big > 0) {
    const remainder = small / divisor;
    if (remainder >= 0.05 && remainder < 0.95) {
      return `${big.toLocaleString()}.${Math.round(remainder * 10)}${
        unit[index]
      }`;
    }
    return `${Math.round(big + remainder).toLocaleString()}${unit[index]}`;
  }

  return small.toLocaleString();
};

export const toParams = (obj: Record<string, string | number>) => {
  const query = Object.entries(obj)
    .filter(([_, value]) => value !== null && value !== undefined)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value as string)}`
    )
    .join('&');

  return query ? `?${query}` : '';
};

export const handleAppFeature = (isGuestChatRoom: boolean) => {
  if (isGuestChatRoom) {
    alert('앱에서만 사용할 수 있는 기능입니다.\n앱을 다운받고 이용해주세요.');
  }
};

export const normalizeVersion = (version: string) =>
  version.split('.').concat('0', '0').slice(0, 3).join('.');

export const revertGoodsNameString = (string: string, isCheming: boolean) => {
  return isCheming
    ? string?.replaceAll(
        CHATIE_SERVICE_STATE.goodsName,
        CHEMING_SERVICE_STATE.goodsName
      )
    : string?.replaceAll(
        CHEMING_SERVICE_STATE.goodsName,
        CHATIE_SERVICE_STATE.goodsName
      );
};
